import { useEffect, useMemo, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";

import { Alert } from "@/components/ui/Alert";
import { Button } from "@/components/ui/Button";
import { Skeleton } from "@/components/ui/Skeleton";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger
} from "@/components/ui/Accordion";
import { ChevronDown } from "lucide-react";
import { formatDollarsRound } from "@/customer/catalog/pricing";
import { PageHeader } from "@/customer/shell/PageHeader";
import { useAuth } from "@/customer/auth/AuthContext";
import { useCart, useCartQuote } from "@/customer/cart/hooks";

import { CheckoutPriceBreakdown } from "./CheckoutPriceBreakdown";
import { CheckoutProgressStrip } from "./CheckoutProgressStrip";
import { SectionHeader } from "./SectionHeader";
import { useCheckoutFlow, type CheckoutStepId } from "./useCheckoutFlow";
import { useFinalize } from "./useFinalize";
import { useCustomerProfile } from "./useCustomerProfile";
import { AccountSection } from "./sections/AccountSection";
import { ApparentAgentSection } from "./sections/ApparentAgentSection";
import { CartContentsSection } from "./sections/CartContentsSection";
import { CoiSection } from "./sections/CoiSection";
import { DeliveryAddressSection } from "./sections/DeliveryAddressSection";
import { DeliveryDateSection } from "./sections/DeliveryDateSection";
import { PaymentSection } from "./sections/PaymentSection";

// Phase 4h checkout-shell — `/checkout` lands here. Single-page,
// Shopify-style: a controlled Radix accordion on the left, sticky
// `<CheckoutPriceBreakdown>` on the right (desktop) or sticky bottom
// drawer (mobile). Each step is a small section component that owns
// its own form + PATCH; the page coordinates which step is open via
// `useCheckoutFlow`.
//
// Out of scope this slice (see the plan): Stripe Elements card
// tokenization (Card radio is disabled), embedded multi-page agreement
// signer (signed on the confirmation page), shadcn Calendar with
// holiday-blackout greying (native date input with min-date in this
// slice).
export function CheckoutPage(): JSX.Element {
  const { user } = useAuth();
  const cartQuery = useCart();
  const quoteQuery = useCartQuote();
  const profileQuery = useCustomerProfile(user !== null);

  if (cartQuery.isPending) return <CheckoutSkeleton />;
  if (cartQuery.isError || !cartQuery.data) {
    return (
      <Alert variant="destructive">
        We couldn&rsquo;t load your cart. Try refreshing the page.
      </Alert>
    );
  }
  if (cartQuery.data.payload.items.length === 0) {
    return <Navigate to="/cart" replace />;
  }

  const cart = cartQuery.data;
  return (
    <CheckoutContent
      cart={cart}
      quote={quoteQuery.data}
      isQuotePending={quoteQuery.isPending}
      profile={profileQuery.data}
    />
  );
}

function CheckoutContent({
  cart,
  quote,
  isQuotePending,
  profile
}: {
  cart: ReturnType<typeof useCart>["data"] & object;
  quote: ReturnType<typeof useCartQuote>["data"];
  isQuotePending: boolean;
  profile: ReturnType<typeof useCustomerProfile>["data"];
}): JSX.Element {
  const { user } = useAuth();
  const navigate = useNavigate();
  const flow = useCheckoutFlow({ cart, user, hasCurrentCoi: false });

  // Auto-open the first incomplete step on first render. We keep the
  // accordion controlled so the customer can click prior steps' headers
  // to re-open and edit; the override is honored as long as the target
  // step is still unlocked (see `useCheckoutFlow`).
  const openValue = flow.openStep ?? flow.orderedSteps[0] ?? null;

  const contactName = profile
    ? [profile.profile.first_name, profile.profile.last_name].filter(Boolean).join(" ")
    : "";
  const contactPhone = profile?.profile.phone ?? "";
  const contactEmail = user?.email ?? "";

  const finalize = useFinalize({ cart, user, contactName, contactPhone, contactEmail });

  async function handleSubmit(): Promise<void> {
    try {
      const res = await finalize.submit();
      if (res.ok && res.order) {
        navigate(`/checkout/confirmed/${res.order.order_number}`);
      }
    } catch {
      // useFinalize already captured the error to `errorMessage`; we
      // swallow here so the click handler doesn't emit an unhandled
      // promise rejection that would surface as a console error in
      // dev or get logged in production.
    }
  }

  function advance(current: CheckoutStepId): () => void {
    return () => {
      const idx = flow.orderedSteps.indexOf(current);
      const next = idx >= 0 ? flow.orderedSteps[idx + 1] : undefined;
      if (next) flow.setOpenStep(next);
    };
  }

  return (
    <div className="pb-32 md:pb-8">
      <div className="mb-6">
        <PageHeader
          eyebrow="Checkout"
          title="Confirm your reservation"
          description="A few details and we&rsquo;ll lock in your delivery."
        />
      </div>

      {/* Phase 4x checkout-polish — horizontal progress strip
          orients the customer at a glance ("Step 3 of 7"). The
          accordion section headers still carry the per-step state
          but a top-of-page strip beats vertical hunting. */}
      <div className="mb-6">
        <CheckoutProgressStrip steps={flow.steps} orderedSteps={flow.orderedSteps} />
      </div>

      <div className="grid gap-6 md:grid-cols-[1fr_320px]">
        <SectionStack
          flow={flow}
          cart={cart}
          openValue={openValue}
          onOpenChange={(v) => flow.setOpenStep(v as CheckoutStepId | null)}
          advance={advance}
        />

        <DesktopSidebar
          quote={quote}
          isQuotePending={isQuotePending}
          canSubmit={flow.canSubmit}
          isSubmitting={finalize.isPending}
          onSubmit={handleSubmit}
          errorMessage={finalize.errorMessage}
        />
      </div>

      <MobileDrawer
        quote={quote}
        isQuotePending={isQuotePending}
        canSubmit={flow.canSubmit}
        isSubmitting={finalize.isPending}
        onSubmit={handleSubmit}
      />
    </div>
  );
}

interface SectionStackProps {
  flow: ReturnType<typeof useCheckoutFlow>;
  cart: ReturnType<typeof useCart>["data"] & object;
  openValue: CheckoutStepId | null;
  onOpenChange(value: string): void;
  advance(current: CheckoutStepId): () => void;
}

function SectionStack({
  flow,
  cart,
  openValue,
  onOpenChange,
  advance
}: SectionStackProps): JSX.Element {
  return (
    <Accordion
      type="single"
      collapsible
      value={openValue ?? ""}
      onValueChange={onOpenChange}
      className="space-y-3"
    >
      {flow.orderedSteps.map((id, idx) => {
        const step = flow.steps[id];
        return (
          <AccordionItem key={id} value={id}>
            <AccordionTrigger disabled={!step.unlocked && !step.complete}>
              <SectionHeader number={idx + 1} step={step} />
            </AccordionTrigger>
            <AccordionContent>
              <SectionBody id={id} cart={cart} onAdvance={advance(id)} />
            </AccordionContent>
          </AccordionItem>
        );
      })}
    </Accordion>
  );
}

function SectionBody({
  id,
  cart,
  onAdvance
}: {
  id: CheckoutStepId;
  cart: ReturnType<typeof useCart>["data"] & object;
  onAdvance(): void;
}): JSX.Element {
  switch (id) {
    case "cart":
      return <CartContentsSection cart={cart} />;
    case "address":
      return <DeliveryAddressSection cart={cart} onAdvance={onAdvance} />;
    case "date":
      return <DeliveryDateSection cart={cart} onAdvance={onAdvance} />;
    case "account":
      return <AccountSection onAdvance={onAdvance} />;
    case "consent":
      return <ApparentAgentSection cart={cart} onAdvance={onAdvance} />;
    case "coi":
      return <CoiSection cart={cart} onAdvance={onAdvance} />;
    case "payment":
      return <PaymentSection cart={cart} onAdvance={onAdvance} />;
    default:
      return <></>;
  }
}

interface SidebarSharedProps {
  quote: ReturnType<typeof useCartQuote>["data"];
  isQuotePending: boolean;
  canSubmit: boolean;
  isSubmitting: boolean;
  onSubmit(): void;
}

function DesktopSidebar({
  quote,
  isQuotePending,
  canSubmit,
  isSubmitting,
  onSubmit,
  errorMessage
}: SidebarSharedProps & { errorMessage: string | null }): JSX.Element {
  return (
    <aside className="hidden md:block md:sticky md:top-20 md:self-start">
      <div className="overflow-hidden rounded-lg border border-zinc-200 bg-brand-white shadow-sm">
        <div className="border-b border-zinc-100 bg-flag-navy px-5 py-3">
          <h2 className="text-xs font-semibold uppercase tracking-[0.18em] text-brand-white">
            Order summary
          </h2>
        </div>
        <div className="space-y-4 p-5">
          <CheckoutPriceBreakdown quote={quote} isPending={isQuotePending} />
          {errorMessage ? (
            <Alert variant="destructive" role="alert">
              {errorMessage}
            </Alert>
          ) : null}
          <Button
            size="lg"
            className="w-full"
            disabled={!canSubmit || isSubmitting}
            onClick={onSubmit}
            data-testid="checkout-submit"
          >
            <ConfirmLabel
              quote={quote}
              canSubmit={canSubmit}
              isSubmitting={isSubmitting}
            />
          </Button>
          <p className="text-xs text-zinc-500">
            By confirming, you agree to JDR&rsquo;s rental terms. We&rsquo;ll
            collect your signed rental agreement after this step.
          </p>
          <HelpPhoneLine />
        </div>
      </div>
    </aside>
  );
}

// Phase 4x checkout-polish — CTA label that surfaces the exact dollar
// amount the customer is about to authorize, matching the grade
// doc's "exact amount on the button: Pay $1,832.14 →" requirement.
// Falls back to the bare action label while the quote is loading,
// is in error, or the customer hasn't completed every step yet (in
// which case the button is disabled anyway).
function ConfirmLabel({
  quote,
  canSubmit,
  isSubmitting
}: {
  quote: ReturnType<typeof useCartQuote>["data"];
  canSubmit: boolean;
  isSubmitting: boolean;
}): JSX.Element {
  if (isSubmitting) return <>Confirming&hellip;</>;
  if (canSubmit && quote && quote.ok) {
    return (
      <>
        Confirm {formatDollarsRound(quote.total_cents)}
        <span aria-hidden="true">&nbsp;&rarr;</span>
      </>
    );
  }
  return <>Confirm reservation</>;
}

// Phase 4x checkout-polish — surface the phone number inside the
// summary sidebar (desktop) + above the mobile drawer button.
// Customers stuck mid-checkout were previously bounced out to find
// the number; the grade doc's "phone link if anything goes wrong"
// requirement applied to confirmation only. Adding it pre-confirm
// catches abandonment from confusion ("how do I do X?").
function HelpPhoneLine(): JSX.Element {
  return (
    <p className="text-xs text-zinc-500">
      Need help?{" "}
      <a
        href="tel:+13034354328"
        className="font-semibold text-brand-red hover:text-brand-red-dark hover:underline"
        data-testid="checkout-help-phone"
      >
        Call (303) 435-4328
      </a>
    </p>
  );
}

function MobileDrawer({
  quote,
  isQuotePending,
  canSubmit,
  isSubmitting,
  onSubmit
}: SidebarSharedProps): JSX.Element {
  const total = quote?.ok ? quote.total_cents : 0;
  const formattedTotal = useMemo(
    () => (quote && quote.ok ? formatDollarsRound(total) : "—"),
    [quote, total]
  );
  const [expanded, setExpanded] = useState(false);

  // Collapse the itemize panel once the submission completes so customers
  // aren't staring at the price breakdown while we navigate.
  useEffect(() => {
    if (!isSubmitting && expanded) setExpanded(false);
  }, [isSubmitting, expanded]);

  return (
    <div className="fixed inset-x-0 bottom-0 z-40 border-t border-zinc-200 bg-brand-white shadow-[0_-8px_24px_rgba(10,10,10,0.08)] md:hidden">
      {expanded ? (
        <div
          id="checkout-mobile-itemize"
          className="max-h-[60vh] overflow-y-auto border-b border-zinc-100 px-4 py-3"
        >
          <CheckoutPriceBreakdown quote={quote} isPending={isQuotePending} />
        </div>
      ) : null}
      <div className="flex items-center justify-between gap-3 px-4 pt-3">
        <div>
          <p className="text-xs uppercase tracking-wide text-zinc-500">Total</p>
          <p className="text-lg font-semibold text-brand-black">{formattedTotal}</p>
        </div>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => setExpanded((v) => !v)}
          aria-expanded={expanded}
          aria-controls="checkout-mobile-itemize"
        >
          {expanded ? "Hide" : "Itemize"}{" "}
          <ChevronDown
            aria-hidden="true"
            className={`ml-1 h-4 w-4 transition-transform ${expanded ? "rotate-180" : ""}`}
          />
        </Button>
      </div>
      <div className="space-y-2 px-4 pb-[max(env(safe-area-inset-bottom),1rem)] pt-3">
        <Button
          size="lg"
          className="w-full"
          disabled={!canSubmit || isSubmitting}
          onClick={onSubmit}
          data-testid="checkout-submit-mobile"
        >
          <ConfirmLabel
            quote={quote}
            canSubmit={canSubmit}
            isSubmitting={isSubmitting}
          />
        </Button>
        <HelpPhoneLine />
      </div>
    </div>
  );
}

function CheckoutSkeleton(): JSX.Element {
  return (
    <div className="space-y-6" aria-busy="true">
      <Skeleton className="h-10 w-64" />
      <div className="grid gap-6 md:grid-cols-[1fr_320px]">
        <div className="space-y-3">
          <Skeleton className="h-16 w-full" />
          <Skeleton className="h-16 w-full" />
          <Skeleton className="h-16 w-full" />
        </div>
        <Skeleton className="h-64 w-full" />
      </div>
    </div>
  );
}
